/**
 * 病歷報表的單欄分頁器。
 *
 * 呼叫端把「一聯」的內容拆成單元序列交進來，分頁器在離屏量測頁上逐單元試塞、量高、切段，
 * 回傳每個元素代表一頁的 HTML 字串。左右聯各自呼叫一次 paginate()，即為兩聯獨立分頁。
 *
 * 契約：
 * - measureCell 必須與正式輸出的儲存格同寬，且與正式頁處在相同的字級與 zoom 環境下，
 *   否則量到的高度與實際輸出不符。
 * - 高度以 measurePage（包住 measureCell 的頁面容器）為準而非 measureCell 本身，
 *   因為報表內容外層帶有 zoom，量儲存格會得到縮放前的值。
 * - 同一個 measureCell 一次只能給一聯使用；paginate() 結束時會自行清空。
 * - 回傳的每一頁都是標籤平衡的片段。這點不可退讓：未關閉的 <table> 寫進 <td> 時，
 *   HTML 剖析器會提早關閉該儲存格，同列後面的儲存格內容會被吞掉。
 */

/**
 * 跨頁包裝。當一段內容由多個單元共用同一個外層標籤（例如醫令列共用一個 <table>）時，
 * suffix 讓換頁前能關閉該標籤，resume 讓續頁能重新開啟它。
 */
export interface PageWrap {
  /** 續頁開頭要補的字串 */
  resume: string;
  /** 換頁前要補在頁尾的字串 */
  suffix: string;
}

export type PageUnit =
  | {
      /** 不可拆單元：放不下就整塊移到下一頁 */
      kind: 'fixed';
      html: string;
      /** 置入本單元「之前」切換包裝；切換時會先用舊的 suffix 收掉目前的包裝 */
      wrapBefore?: PageWrap;
      /** 置入本單元「之後」切換包裝 */
      wrapAfter?: PageWrap;
    }
  | {
      /** 可拆單元：以字元為單位切段，跨頁接續 */
      kind: 'text';
      text: string;
      /** isFirstPart 用於只有首段才該出現的前綴（例如 PE:） */
      render: (part: string, isFirstPart: boolean) => string;
    };

export class ReportPagePaginator {
  /** 二分法迭代上限，2^14 已涵蓋任何主訴長度 */
  private static readonly BISECT_LIMIT = 14;

  constructor(
    private measureCell: HTMLElement,
    private measurePage: HTMLElement,
    private maxHeight: number
  ) {}

  /** 把 html 放進量測儲存格，回傳整頁是否仍在頁高限制內 */
  private fits(html: string): boolean {
    this.measureCell.innerHTML = html;
    return this.measurePage.offsetHeight <= this.maxHeight;
  }

  /** 二分法求出接在 current 之後還放得下的最大字數；回傳 0 表示一個字都放不下 */
  private fitTextLength(
    current: string,
    text: string,
    render: (part: string, isFirstPart: boolean) => string,
    isFirstPart: boolean,
    suffix: string
  ): number {
    if (this.fits(current + render(text, isFirstPart) + suffix)) {
      return text.length;
    }
    let low = 1;
    let high = text.length;
    let best = 0;
    for (let k = 0; k < ReportPagePaginator.BISECT_LIMIT && low <= high; k++) {
      const mid = Math.floor((low + high) / 2);
      if (this.fits(current + render(text.slice(0, mid), isFirstPart) + suffix)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return best;
  }

  /** 把單元序列切成每格一頁的 HTML；至少回傳一頁，每頁標籤平衡 */
  paginate(units: PageUnit[]): string[] {
    const pages: string[] = [];
    let cur = '';
    let resume = '';
    let suffix = '';

    for (const unit of units) {
      if (unit.kind === 'fixed') {
        if (unit.wrapBefore !== undefined) {
          cur += suffix;                 // 先收掉目前的包裝，本單元起不再屬於它
          resume = unit.wrapBefore.resume;
          suffix = unit.wrapBefore.suffix;
        }
        // cur === resume 代表本頁尚未放入任何內容，此時再放不下也只能放，否則會無限換頁
        if (cur !== resume && !this.fits(cur + unit.html + suffix)) {
          pages.push(cur + suffix);
          cur = resume;
        }
        cur += unit.html;
        if (unit.wrapAfter !== undefined) {
          resume = unit.wrapAfter.resume;
          suffix = unit.wrapAfter.suffix;
        }
      } else {
        let rest = unit.text;
        let isFirstPart = true;
        while (rest.length > 0) {
          const len = this.fitTextLength(cur, rest, unit.render, isFirstPart, suffix);
          if (len <= 0) {
            if (cur === resume) {
              // 空頁也放不下一個字，強制放一個字避免無限迴圈
              cur += unit.render(rest.slice(0, 1), isFirstPart);
              rest = rest.slice(1);
              isFirstPart = false;
              continue;
            }
            pages.push(cur + suffix);
            cur = resume;
            continue;
          }
          cur += unit.render(rest.slice(0, len), isFirstPart);
          rest = rest.slice(len);
          isFirstPart = false;
          if (rest.length > 0) {
            pages.push(cur + suffix);
            cur = resume;
          }
        }
      }
    }

    pages.push(cur + suffix);
    this.measureCell.innerHTML = '';
    return pages;
  }
}
